'use client';

import { useState, useEffect, useRef } from 'react';
import { Wifi, Car, TreePine, Users, Baby, Chrome as Home, MapPin, Star } from 'lucide-react';

const Amenities = (): JSX.Element => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const amenities = [
    { icon: Wifi, title: 'High-Speed WiFi', description: 'Stay connected with blazing-fast internet throughout the property' },
    { icon: Car, title: 'Private Parking', description: 'Secure parking spaces for multiple vehicles' },
    { icon: TreePine, title: 'Garden Views', description: 'Breathtaking landscaped grounds and natural scenery' },
    { icon: Users, title: 'Group Friendly', description: 'Perfect for large families and group gatherings' },
    { icon: Baby, title: 'Family Features', description: 'Baby cribs and family-friendly amenities available' },
    { icon: Home, title: 'Multiple Porches', description: 'Relaxing outdoor spaces with scenic views' },
    { icon: MapPin, title: 'Religious Facilities', description: 'On-site shul and mikveh available' },
    { icon: Star, title: 'Prime Location', description: 'Minutes away from shopping, dining, and attractions' }
  ];

  return (
    <section id="amenities" ref={sectionRef} className="py-20 bg-[#F9F6F2] relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`transform transition-all duration-1000 ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* Image Side */}
            <div className="relative">
              <div className="relative overflow-hidden rounded-lg shadow-2xl">
                <img
                  src="https://kujksybmpszaxvhhcyjb.supabase.co/storage/v1/object/public/tmvvilla/ahmad-ajmi-2Wqit9jCNeY-unsplash.jpg"
                  alt="Luxury Living Room"
                  className="w-full h-96 lg:h-[600px] object-cover transition-transform duration-700 hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-[#7C8F75]/20 to-transparent"></div>
              </div>
              
              {/* Floating Stats */}
              <div className="absolute -bottom-8 -right-8 bg-white p-6 rounded-lg shadow-xl border-l-4 border-[#7C8F75]">
                <div className="text-center">
                  <div className="text-3xl font-bold text-[#7C8F75]">5★</div>
                  <div className="text-sm text-[#333333] font-medium">Premium Rating</div>
                </div>
              </div>
            </div>

            {/* Content Side */}
            <div>
              <div className="mb-12">
                <h2 className="text-4xl md:text-5xl font-bold text-[#333333] mb-6">
                  Property Amenities
                </h2>
                <p className="text-xl text-[#333333]/80 leading-relaxed">
                  Mountain View offers a range of amenities to ensure a comfortable stay. 
                  The property includes modern conveniences and thoughtful touches 
                  for families and groups of all sizes.
                </p>
              </div>

              {/* Amenities Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                {amenities.map((amenity, index) => (
                  <div
                    key={index}
                    className={`group bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-all duration-300 transform hover:scale-105 hover:bg-[#7C8F75]/5 delay-${index * 50}`}
                  >
                    <div className="flex items-start space-x-4">
                      <div className="bg-[#7C8F75]/10 p-3 rounded-lg group-hover:bg-[#7C8F75] transition-colors duration-300">
                        <amenity.icon className="h-6 w-6 text-[#7C8F75] group-hover:text-white transition-colors duration-300" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-[#333333] mb-2 group-hover:text-[#7C8F75] transition-colors duration-300">
                          {amenity.title}
                        </h3>
                        <p className="text-[#333333]/70 text-sm leading-relaxed">
                          {amenity.description}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Special Features Highlight */}
              <div className="mt-12 p-6 bg-[#7C8F75]/10 rounded-lg border-l-4 border-[#7C8F75]">
                <h3 className="text-xl font-bold text-[#333333] mb-3">
                  What Makes Us Special
                </h3>
                <ul className="space-y-2 text-[#333333]/80">
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>Only 4 minutes from KJ Munro community</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>7 minutes to Woodbury Commons Premium Outlets</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>5 minutes to רוח החיים Park for outdoor activities</span>
                  </li>
                  <li className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-[#7C8F75] rounded-full"></div>
                    <span>On-site religious facilities for your spiritual needs</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Wave transition to Booking section */}
      <div className="absolute bottom-0 left-0 w-full overflow-hidden leading-none z-20">
        <svg 
          className="relative block w-full h-32" 
          data-name="Layer 1" 
          xmlns="http://www.w3.org/2000/svg" 
          viewBox="0 0 1200 120" 
          preserveAspectRatio="none"
        >
          <defs>
            <linearGradient id="amenitiesWaveGradient1" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="#F9F6F2" stopOpacity="1" />
              <stop offset="20%" stopColor="rgba(249, 246, 242, 0.9)" stopOpacity="0.9" />
              <stop offset="40%" stopColor="rgba(255, 255, 255, 0.7)" stopOpacity="0.7" />
              <stop offset="60%" stopColor="rgba(255, 255, 255, 0.8)" stopOpacity="0.8" />
              <stop offset="80%" stopColor="rgba(255, 255, 255, 0.95)" stopOpacity="0.95" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="amenitiesWaveGradient2" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(232, 227, 221, 0.8)" stopOpacity="0.8" />
              <stop offset="30%" stopColor="rgba(124, 143, 117, 0.05)" stopOpacity="0.3" />
              <stop offset="60%" stopColor="rgba(255, 255, 255, 0.6)" stopOpacity="0.6" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
            </linearGradient>
            <linearGradient id="amenitiesWaveGradient3" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(249, 246, 242, 0.6)" stopOpacity="0.6" />
              <stop offset="50%" stopColor="rgba(255, 255, 255, 0.9)" stopOpacity="0.9" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="1" />
            </linearGradient>
          </defs>
          
          {/* Base wave */}
          <path 
            d="M0,60L48,65C96,70,192,80,288,75C384,70,480,50,576,45C672,40,768,50,864,60C960,70,1056,80,1152,75C1200,70,1248,50,1296,45L1344,40L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#amenitiesWaveGradient1)"
          />
          
          {/* Second wave layer */}
          <path 
            d="M0,40L48,45C96,50,192,60,288,55C384,50,480,30,576,25C672,20,768,30,864,40C960,50,1056,60,1152,55C1200,50,1248,30,1296,25L1344,20L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#amenitiesWaveGradient2)"
          />
          
          {/* Final wave */}
          <path 
            d="M0,20L48,25C96,30,192,40,288,35C384,30,480,10,576,5C672,0,768,10,864,20C960,30,1056,40,1152,35C1200,30,1248,10,1296,5L1344,0L1344,120L1296,120C1248,120,1152,120,1056,120C960,120,864,120,768,120C672,120,576,120,480,120C384,120,288,120,192,120C96,120,48,120,24,120L0,120Z" 
            fill="url(#amenitiesWaveGradient3)"
          />
        </svg>
      </div>
    </section>
  );
};

export default Amenities;